// We cannot use the useSearchParams function from 'react-router'
// because we are not always in a <Router> component context.
// We must instead only optionally create/initialize react state from window locations.

import { useCallback, useState } from 'react';
import {
  type SetURLSearchParams,
  type URLSearchParamsInit,
  createSearchParams,
  type useSearchParams as reactRouterUseSearchParams,
} from 'react-router';

/** Returns a URLSearchParams from the window if it exists, or a new URLSearchParams if one cannot be found. */
function latestSearchParams(): URLSearchParams {
  if (
    'window' in globalThis &&
    typeof globalThis.window === 'object' &&
    'location' in globalThis.window &&
    typeof globalThis.window.location === 'object' &&
    'href' in globalThis.window.location &&
    typeof globalThis.window.location.href === 'string'
  ) {
    return createSearchParams(globalThis.window.location.search);
  }
  return new URLSearchParams();
}

/**
 * Updates window history with new search params, or no-ops if window does not exist.
 * These changes will trigger on [window.history events](https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event).
 * @param newParams The new params to set:
 * * `undefined` is a no-op.
 * * `null` clears all current search params.
 * * Anything else completely overwrites search params.
 * @returns a new URLSearchParams object with all current search params.
 */
function setSearchParams(newParams?: URLSearchParams | null): URLSearchParams {
  if (
    'window' in globalThis &&
    typeof globalThis.window === 'object' &&
    'location' in globalThis.window &&
    typeof globalThis.window.location === 'object' &&
    'href' in globalThis.window.location &&
    typeof globalThis.window.location.href === 'string'
  ) {
    if (newParams === undefined) {
      // no-op
      return new URLSearchParams();
    }
    // we are either setting or clearing the search
    const url = new URL(globalThis.window.location.href);
    if (newParams === null) {
      url.search = '';
    } else {
      url.search = newParams.toString();
    }
    globalThis.window.history.pushState({}, '', url);
    return new URLSearchParams(url.search);
  }
  return new URLSearchParams();
}

/**
 * Updates window history with new search params.
 * These changes will trigger on [window.history events](https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event).
 * @param fn a callback to update all current search params, the first `currentParams` of the callback has all current values in the window (if one exists globally).
 * @returns a new URLSearchParams object with all latest set search params.
 */
export function updateSearchParams(
  fn: (currentParams: URLSearchParams) => URLSearchParams | null,
): URLSearchParams {
  return setSearchParams(fn(latestSearchParams()));
}

/**
 * A hook for managing and updating against searchParams.
 * Allows us to mimic react-router useSearchParams in a non-react-router context.
 * @warning this might not update react-router on updates, and this might not trigger on react-router updates url updates.
 * These changes will still trigger on [window.history events](https://developer.mozilla.org/en-US/docs/Web/API/Window/popstate_event).
 * @warning The secondary `options`: `NavigateOptions` param is ignored.
 */
export const useSearchParams: typeof reactRouterUseSearchParams = (searchParamsInit) => {
  const [searchParams, setSearchParamsInternal] = useState(
    searchParamsInit ? createSearchParams(searchParamsInit) : latestSearchParams(),
  );
  const setUrlSearchParams: SetURLSearchParams = useCallback(
    (nextInit?: URLSearchParamsInit | ((prev: URLSearchParams) => URLSearchParamsInit)) => {
      const newParams: URLSearchParams =
        typeof nextInit === 'function' // if we are a function, always pull the latest information from the window
          ? createSearchParams(nextInit(latestSearchParams()))
          : createSearchParams(nextInit);
      setSearchParamsInternal(newParams);
      return setSearchParams(createSearchParams(newParams));
    },
    [],
  );
  return [searchParams, setUrlSearchParams];
};
